import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import sh from "bundles/common/utils/sh";
import {
  dialogLabelSx,
  dialogInputSx,
  selectSx,
  menuProps,
  ghostButtonSx,
  primaryButtonSx,
} from "bundles/Internal/components/common/FloatingTimer";

export default function CreateTaskDialog({
  open,
  onClose,
  context,
  selectedProjectId,
  coloredProjects,
  authenticity_token,
  onCreated,
}) {
  const [newTaskName, setNewTaskName] = useState("");
  const [taskDialogProjectId, setTaskDialogProjectId] = useState("");

  useEffect(() => {
    if (!open) return;
    setNewTaskName("");
    setTaskDialogProjectId(context === "manual" ? (selectedProjectId || "") : "");
  }, [open, context, selectedProjectId]);

  const targetProjectId = context === "manual" ? (taskDialogProjectId || "") : selectedProjectId;

  const handleClose = () => {
    setNewTaskName("");
    setTaskDialogProjectId("");
    onClose();
  };

  const handleCreate = () => {
    if (!newTaskName.trim() || !targetProjectId) return;
    sh.post("/internal/time_tasks", {
      authenticity_token,
      time_task: { name: newTaskName.trim(), time_project_id: targetProjectId },
    }).then((res) => {
      onCreated(res.data, targetProjectId);
      handleClose();
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { borderRadius: "16px", minWidth: 360, maxWidth: 400 } }}
    >
      <DialogTitle
        sx={{
          fontSize: 16,
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          color: "hsl(150 30% 12%)",
          pb: 0.5,
          pt: 2.5,
          px: 3,
        }}
      >
        Create task
      </DialogTitle>
      <DialogContent sx={{ pt: 1.5, px: 3, pb: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        {context === "manual" && (
          <Box>
            <Typography sx={dialogLabelSx}>Project</Typography>
            <Select
              value={taskDialogProjectId}
              onChange={(e) => setTaskDialogProjectId(e.target.value)}
              displayEmpty
              size="small"
              variant="outlined"
              MenuProps={menuProps}
              sx={selectSx}
              renderValue={(value) => {
                if (!value) {
                  return (
                    <Typography sx={{ color: "hsl(150 10% 46%)", fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>Select project</Typography>
                  );
                }
                const p = coloredProjects.find((cp) => cp.id === value);
                if (!p) return null;
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
                      {p.name}{p.company?.name ? ` — ${p.company.name}` : ""}
                    </Typography>
                  </Box>
                );
              }}
            >
              {coloredProjects.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
                      {p.name}{p.company?.name ? ` — ${p.company.name}` : ""}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        <Box>
          <Typography sx={dialogLabelSx}>Task name</Typography>
          <Box
            component="input"
            placeholder="e.g. CV screening"
            value={newTaskName}
            onChange={(e) => setNewTaskName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newTaskName.trim() && targetProjectId) handleCreate();
            }}
            autoFocus
            sx={dialogInputSx}
          />
          {(() => {
            if (context === "manual") return null;
            const proj = coloredProjects.find((cp) => cp.id === selectedProjectId);
            if (!proj) return null;
            return (
              <Typography sx={{ mt: 0.75, fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
                Will be added to <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>{proj.name}</Box>
              </Typography>
            );
          })()}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button onClick={handleClose} sx={ghostButtonSx}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disableElevation
          disabled={!newTaskName.trim() || !targetProjectId}
          sx={primaryButtonSx}
        >
          Create task
        </Button>
      </DialogActions>
    </Dialog>
  );
}
